import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18next from 'i18next';
import get from 'lodash.get';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import { isResizableImage } from 'lib/isAimsUrl';

import { video as videoPropType } from 'lib/CustomPropTypes';

import LazyLoad from 'components/LazyLoad';
import Picture from 'components/Picture';

export class VideoSlate extends Component {
  static propTypes = {
    centerPlayButtonAtMobile: PropTypes.bool,
    isContentPlaying: PropTypes.bool,
    isLive: PropTypes.bool,
    isLoading: PropTypes.bool,
    isRailLayout: PropTypes.bool,
    onPlayClick: PropTypes.func,
    pageView: PropTypes.string.isRequired,
    playButtonStyle: PropTypes.string,
    positionStyleValue: PropTypes.string,
    showPlayIcon: PropTypes.bool,
    showReplayIcon: PropTypes.bool,
    showTeaseImage: PropTypes.bool,
    video: videoPropType.isRequired,
  };

  static defaultProps = {
    centerPlayButtonAtMobile: false,
    isContentPlaying: false,
    isLive: false,
    isLoading: false,
    isRailLayout: false,
    onPlayClick: () => {},
    playButtonStyle: null,
    positionStyleValue: 'relative',
    showPlayIcon: true,
    showReplayIcon: false,
    showTeaseImage: true,
  };

  // display overlay div for expired videos
  showExpiredOverlay = () => (
    <div
      className="expiredOverlay items-center df absolute w-100 h-100 z-2 f7 f8-m"
      data-test="video-player__expired-overlay"
    >
      <div className="expiredText founders-cond tc w-100 gray-60">
        { i18next.t('We apologize, this video has expired.') }
      </div>
    </div>
  );

  replayIcon() {
    const {
      isContentPlaying,
      showReplayIcon,
      isLoading,
      centerPlayButtonAtMobile,
    } = this.props;

    if (showReplayIcon && !isContentPlaying && !isLoading) {
      return (
        <div
          className={classNames(
            'absolute absolute-fill w100 dn-print df replayIconContainer',
            {
              'items-center items-center-m justify-center justify-center-m': !centerPlayButtonAtMobile,
              'items-center justify-center': centerPlayButtonAtMobile,
            },
          )}
          data-test="video-player__replay-button__outer"
        >
          <span className="replayIcon z-2 white icon icon-replay" />
        </div>
      );
    }
    return null;
  }

  playIcon() {
    const {
      centerPlayButtonAtMobile,
      isContentPlaying,
      isLive,
      isLoading,
      isRailLayout,
      playButtonStyle,
      showPlayIcon,
    } = this.props;

    if (showPlayIcon && !isContentPlaying && !isLoading) {
      return (
        <div
          className="playButtonOuter absolute absolute-fill w100 dn-print"
          data-test="video-player__play-button__outer"
        >
          <div
            className={classNames(
              'absolute absolute-fill w100 df',
              {
                'items-center items-center-m justify-center justify-center-m': !centerPlayButtonAtMobile,
                'items-center justify-center': centerPlayButtonAtMobile,
              },
            )}
          >
            <div
              className={classNames(
                'play',
                {
                  'play--live': isLive,
                  playButtonStyle,
                  'play--rail-layout': isRailLayout,
                },
                'df items-center justify-center tc lh-none',
              )}
            >
              <svg
                className="play--icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 15 15"
                preserveAspectRatio="xMidYMid"
                style={{ maxWidth: '30px' }}
              >
                <path d="M0 0v15l15-7.41z" fillRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  teaseImage() {
    const { showTeaseImage } = this.props;
    if (!showTeaseImage) {
      return null;
    }

    const {
      video,
      pageView,
      onPlayClick,
    } = this.props;

    const img = get(video, 'primaryImage') || get(video, 'mainImage');
    const imgSrc = get(img, 'url.primary') || get(img, 'url') || null;
    const href = get(video, 'urls.canonical') || get(video, 'url.primary') || null;
    const altText = get(img, 'altText') || null;

    let picture = <img src={imgSrc} alt={altText} />;

    // If resizeable (AIMS or Telemundo) image, use Picture component.
    if (isResizableImage(imgSrc)) {
      picture = (
        <Picture
          responsiveFlavors={{
            s: AIMS_FLAVORS.FOCAL_760x428, // 16x9 ratio (56.25%)
            m: AIMS_FLAVORS.FOCAL_1000x563,
            xl: AIMS_FLAVORS.FOCAL_860x484,
          }}
          url={imgSrc}
          alt={altText}
          useSuffix
          aimsOptions={{ mode: 5 }}
        />
      );
      if (pageView !== 'article') {
        picture = <LazyLoad placeholder={false}>{picture}</LazyLoad>;
      }
    }

    // This <a> requires href from a structural perspective, so bypass click blocking in preview.
    return (
      <a
        href={href}
        onClick={onPlayClick}
        className="allowClicksOnPreview"
      >
        {picture}
      </a>
    );
  }

  render() {
    const {
      showTeaseImage,
      showPlayIcon,
      showReplayIcon,
    } = this.props;

    // Requires tease image
    if (!showTeaseImage) return null;

    const {
      video: {
        playable,
        isContinuousPlay,
      },
      positionStyleValue,
    } = this.props;

    const teaseImage = this.teaseImage();

    // check to see if video is playable or expired. If expired, render overlay
    let videoPlayable = true;
    if (typeof playable !== 'undefined') {
      videoPlayable = playable;
    }
    if (typeof isContinuousPlay !== 'undefined') {
      videoPlayable = videoPlayable || isContinuousPlay;
    }

    const videoSlateClass = classNames('videoSlate', positionStyleValue);

    if (teaseImage !== null) {
      return (
        <div className={videoSlateClass}>
          {!videoPlayable && this.showExpiredOverlay()}
          {teaseImage}
          {videoPlayable && showPlayIcon && this.playIcon()}
          {videoPlayable && showReplayIcon && this.replayIcon()}
        </div>
      );
    }

    if (showReplayIcon) {
      return this.replayIcon();
    }

    return this.playIcon();
  }
}

export default VideoSlate;
